/**
 * WorkspaceEngine — pluggable entry point for SOLVE + 5-HOW practice.
 *
 * Usage:
 *   <WorkspaceEngine item={practiceItem} onClose={fn} />
 *
 * practiceItem shape:
 *   {
 *     sourceType: 'question' | 'lesson' | 'topic',
 *     sourceKey: string,
 *     title: string,
 *     prompt: string,
 *     availableFrameworks?: ['solve', 'five-how'],   // defaults to both
 *     context?: {
 *       task_type: 'python' | 'data-analysis' | 'sql' | 'excel' | 'generic',
 *       task_description?: string,
 *       sample_data?: string | object,
 *       schema?: { [column]: dtype },
 *     },
 *     guidance?: {
 *       solve?: { split?: string, observe?: string, look?: string, verify?: string, evolve?: string },
 *       'five-how'?: { practical?: string, logic?: string, analogy?: string, story?: string, visual?: string },
 *     },
 *     stepOverrides?: {
 *       solve?: { [stepKey]: { title?: string, instruction?: string, hint?: string } },
 *     },
 *   }
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Tab, Tabs,
  Stack, Typography,
} from '@mui/material';
import { gradePracticeAttempt } from '../../api/practiceApi';
import { useWorkspaceState } from './hooks/useWorkspaceState';
import SOLVEWorkspace from './SOLVEWorkspace';
import FiveHowWorkspace from './FiveHowWorkspace';
import PracticeGradeResult from './PracticeGradeResult';

const WorkspaceEngine = ({ item, onClose, onComplete }) => {
  const availableFrameworks = item?.availableFrameworks?.length > 0
    ? item.availableFrameworks
    : ['solve', 'five-how'];
  const hasSolve = availableFrameworks.includes('solve');
  const hasFiveHow = availableFrameworks.includes('five-how');

  const [framework, setFramework] = useState(hasSolve ? 'solve' : 'five-how');
  const [grading, setGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState(null);
  const [gradeError, setGradeError] = useState('');

  const {
    attempt,
    responses,
    loading,
    error,
    saveStatus,
    answeredCount,
    isCompleted,
    updateStepResponse,
    save,
  } = useWorkspaceState({ item, framework, onAutoSaved: onComplete });

  // Grading is per attempt (framework-specific), so switching tabs should not
  // show stale results from the other framework's attempt.
  useEffect(() => {
    setGradeResult(null);
    setGradeError('');
  }, [framework]);

  const handleComplete = async () => {
    const saved = await save('completed');
    if (saved && onComplete) {
      onComplete(responses, framework);
    }
  };

  const handleGrade = async () => {
    if (!attempt?.attempt_id) return;
    setGrading(true);
    setGradeError('');
    try {
      const graded = await gradePracticeAttempt(attempt.attempt_id);
      setGradeResult(graded);
    } catch (e) {
      setGradeError(e.message || 'Could not grade this attempt right now.');
    } finally {
      setGrading(false);
    }
  };

  const confirmAndClose = () => {
    if (saveStatus === 'saving') return;
    onClose?.();
  };

  const currentModeLabel = framework === 'solve' ? 'SOLVE: understand the scenario' : '5-HOW: deepen the idea';

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex', flexDirection: 'column', height: '100%',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 0, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
              Guided Practice
            </Typography>
            <Typography variant="h6" fontWeight={800}>{item?.title || 'Practice'}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
              {['Lesson page', 'Optional practice', currentModeLabel].map((label, index) => (
                <React.Fragment key={label}>
                  <Chip
                    size="small"
                    label={label}
                    color={index === 2 ? 'primary' : 'default'}
                    variant={index === 2 ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                  />
                  {index < 2 && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 800 }}>
                      {'->'}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexShrink: 0 }}>
            <Button size="small" onClick={confirmAndClose}>← Back</Button>
          </Stack>
        </Stack>

        {hasSolve && hasFiveHow && (
          <>
            <Tabs
              value={framework}
              onChange={(_, value) => setFramework(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 42,
                '& .MuiTab-root': {
                  minHeight: 42,
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '0.78rem',
                },
              }}
            >
              <Tab value="solve" label="1. SOLVE: understand the scenario" />
              <Tab value="five-how" label="2. 5-HOW: deepen the idea" />
            </Tabs>
            <Alert severity="info" icon={false} sx={{ my: 1.25, py: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                This is optional rehearsal. Start with <strong>SOLVE</strong> when the task feels unclear:
                split the scenario, identify what is given, choose tools, prove the answer, then improve it.
                Use <strong>5-HOW</strong> only when the idea still needs to click: tiny code test, plain-English logic,
                real-life comparison, job story, or extra practice. Your full project work stays in GitHub.
              </Typography>
            </Alert>
          </>
        )}
      </Box>

      {/* Body — scrolls within the full-screen dialog */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <Box>
          {framework === 'solve' ? (
            <SOLVEWorkspace
              item={item}
              responses={responses}
              onResponseChange={updateStepResponse}
              onComplete={handleComplete}
              saveStatus={saveStatus}
            />
          ) : (
            <FiveHowWorkspace
              item={item}
              responses={responses}
              onResponseChange={updateStepResponse}
              onComplete={handleComplete}
              saveStatus={saveStatus}
            />
          )}

          {isCompleted && (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
              <Alert severity="success" icon={false}>
                <Typography variant="body2" fontWeight={700}>
                  {framework === 'solve' ? 'SOLVE completed ✓' : 'Practice completed ✓'}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                    — edit any step to update your journal entry
                    {framework === 'solve' && hasFiveHow && <>, or open <strong>5-HOW: deepen the idea</strong> when the concept still needs to click</>}
                  </Typography>
                </Typography>
              </Alert>
            </Box>
          )}

          {answeredCount > 0 && (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGrade}
                  disabled={grading || !attempt?.attempt_id}
                >
                  {grading ? 'Grading…' : gradeResult ? 'Re-grade my practice' : 'Grade my practice'}
                </Button>
                {gradeError && <Alert severity="warning" sx={{ py: 0 }}>{gradeError}</Alert>}
              </Stack>
              {gradeResult && (
                <PracticeGradeResult sx={{ mt: 1.5 }} grade={gradeResult} />
              )}
            </Box>
          )}
        </Box>
      )}
      </Box>
    </Paper>
  );
};

export default WorkspaceEngine;
